import { useQuery, useMutation, useQueryClient } from '../lib/queryClient';
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { getChatMessages, ChatMessage } from '../endpoints/chat/list_GET.schema';
import { sendChatMessage } from '../endpoints/chat/send_POST.schema';

const CHAT_POLL_INTERVAL = 2000;
const LAST_READ_STORAGE_KEY = 'chat-last-read-';

/**
 * Get the last read timestamp for a session from localStorage
 */
function getLastReadTimestamp(sessionId: string): number {
  try {
    const stored = localStorage.getItem(LAST_READ_STORAGE_KEY + sessionId);
    return stored ? parseInt(stored, 10) : 0;
  } catch {
    return 0;
  }
}

/**
 * Set the last read timestamp for a session in localStorage
 */
function setLastReadTimestamp(sessionId: string, timestamp: number): void {
  try {
    localStorage.setItem(LAST_READ_STORAGE_KEY + sessionId, String(timestamp));
  } catch {
    // Ignore localStorage errors
  }
}

export const useChat = (sessionId: string | undefined) => {
  const queryClient = useQueryClient();

  // We keep track of all messages in a local state to append new ones from polling
  // However, for simplicity and consistency with React Query, we can just rely on the cache
  // But since we want to poll only *new* messages (using 'after'), we need a slightly more complex setup
  // or we can just fetch all recent messages every time if the volume is low.
  // Given the requirement "Track the last message ID to only fetch new messages", we will implement incremental fetching.

  // Actually, standard useQuery with polling replaces the data.
  // To support incremental updates, we can use infinite query or just merge data manually.
  // A simpler approach for this specific requirement without over-engineering:
  // 1. Initial load fetches recent messages.
  // 2. Polling fetches messages after the last known ID.
  // 3. We merge the results.

  // However, React Query's data merging is best handled via setQueryData.

  // Let's use a standard query for the initial load and "base" list.
  // And a separate effect for polling new items? No, that's messy.

  // Better approach:
  // The query key will be ["chat", sessionId].
  // The queryFn will fetch *all* needed messages.
  // But wait, the requirement says "Input schema: { ... after?: string }".
  // This implies we should use this for optimization.

  // Let's stick to a simpler model first:
  // We maintain the list of messages in the query cache.
  // The query function will look at the current cache, find the last ID, and ask for messages after that.
  // Then it returns the *merged* list.

  return useQuery({
    queryKey: ['chat', sessionId],
    queryFn: async ({ queryKey }) => {
      if (!sessionId) return [];

      // Get current data from cache to find the last message ID
      const currentData = queryClient.getQueryData<ChatMessage[]>(queryKey) || [];
      const lastMessage = currentData[currentData.length - 1];
      const afterId = lastMessage?.id;

      // Fetch new messages
      const response = await getChatMessages({
        sessionId,
        after: afterId,
      });

      // If we have new messages, append them to the existing ones
      // If we don't have existing data (first load), just return what we got
      if (currentData.length === 0) {
        return response.messages;
      }

      // Deduplicate just in case (though 'after' should handle it)
      const newMessages = response.messages.filter(
        (msg) => !currentData.some((existing) => existing.id === msg.id),
      );

      return [...currentData, ...newMessages];
    },
    enabled: !!sessionId,
    refetchInterval: CHAT_POLL_INTERVAL,
    // We need to preserve data during refetches to allow the incremental logic to work
    placeholderData: (previousData) => previousData,
    // Don't refetch on window focus as we are polling
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook to track unread message count for team chat
 * Uses the chat query to get messages and tracks last read timestamp in localStorage
 * Returns unread count and a function to mark messages as read
 */
export const useUnreadChatCount = (sessionId: string | undefined) => {
  const queryClient = useQueryClient();
  const [lastReadTimestamp, setLastReadTimestampState] = useState<number>(() => {
    return sessionId ? getLastReadTimestamp(sessionId) : 0;
  });

  // Re-initialize when sessionId changes
  useEffect(() => {
    if (sessionId) {
      setLastReadTimestampState(getLastReadTimestamp(sessionId));
    }
  }, [sessionId]);

  // Use useQuery to subscribe to chat data changes
  const { data: messages } = useQuery({
    queryKey: ['chat', sessionId],
    queryFn: () => [] as ChatMessage[], // This won't actually fetch, just subscribes to existing data
    enabled: false, // Don't fetch, just use cached data
  });

  // Get cached messages directly from query client
  const cachedMessages = queryClient.getQueryData<ChatMessage[]>(['chat', sessionId]) || [];
  const authData = queryClient.getQueryData<any>(['auth', 'session']);
  const currentUserId = authData?.id;

  // Calculate unread count from messages after last read timestamp (excluding own messages)
  const unreadCount = useMemo(() => {
    if (!sessionId || cachedMessages.length === 0) return 0;

    return cachedMessages.filter((msg) => {
      const msgTime = new Date(msg.createdAt).getTime();
      return msgTime > lastReadTimestamp && msg.userId !== currentUserId;
    }).length;
  }, [cachedMessages, lastReadTimestamp, currentUserId, sessionId]);

  // Subscribe to query cache updates to trigger re-renders when messages change
  const [, forceUpdate] = useState(0);
  useEffect(() => {
    if (!sessionId) return;

    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (event.query.queryKey[0] === 'chat' && event.query.queryKey[1] === sessionId) {
        forceUpdate((n) => n + 1);
      }
    });

    return unsubscribe;
  }, [sessionId, queryClient]);

  // Mark all messages as read
  const markAsRead = useCallback(() => {
    if (!sessionId) return;

    const now = Date.now();
    setLastReadTimestampState(now);
    setLastReadTimestamp(sessionId, now);
  }, [sessionId]);

  return { unreadCount, markAsRead };
};

export const useSendMessage = (sessionId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (message: string) => sendChatMessage({ sessionId, message }),
    onMutate: async (newMessageText) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: ['chat', sessionId] });

      // Snapshot the previous value
      const previousMessages = queryClient.getQueryData<ChatMessage[]>(['chat', sessionId]);

      // Optimistically update to the new value
      // We need a temporary ID and user info.
      // Since we don't have the user info easily accessible here without passing it in or using another hook,
      // we might skip full optimistic update of the *user details* if we want to be 100% safe,
      // OR we can rely on the fact that the mutation returns the full object quickly.
      // But for a "snappy" feel, let's try to construct a fake message if possible.
      // However, we don't have the current user's avatar/name here easily without `useAuth`.
      // So let's just rely on the fast mutation response for the UI update,
      // OR we can assume the UI handles "pending" state.

      // Actually, the requirement says "The mutation should optimistically add the message to the cache".
      // To do this properly, we'd need the current user info.
      // Let's assume the UI will handle the loading state or we accept a slight delay.
      // BUT, if we strictly follow "optimistically add", we need to mock the user.
      // Since we can't easily get the user here without coupling, let's do a "semi-optimistic" update
      // where we might not have full user details, or we just wait for success.

      // Re-reading: "The mutation should optimistically add the message to the cache"
      // I will implement this by assuming the consumer might want to pass the user info,
      // or we just wait for the mutation result which is usually fast enough.
      // However, standard optimistic updates usually require the data.

      // Let's stick to standard invalidation for simplicity and robustness unless we have the user object.
      // Wait, I can get the user from the auth cache!
      const authData = queryClient.getQueryData<any>(['auth', 'session']);

      if (authData && authData.id) {
        // It's the user object
        const optimisticMessage: ChatMessage = {
          id: 'temp-' + Date.now(),
          sessionId,
          userId: authData.id,
          message: newMessageText,
          createdAt: new Date(),
          user: {
            id: authData.id,
            displayName: authData.displayName,
            avatarUrl: authData.avatarUrl,
          },
        };

        queryClient.setQueryData<ChatMessage[]>(['chat', sessionId], (old) => {
          return [...(old || []), optimisticMessage];
        });

        return { previousMessages, tempId: optimisticMessage.id };
      }

      return { previousMessages };
    },
    onError: (err, newMessage, context) => {
      // Rollback
      if (context?.previousMessages) {
        queryClient.setQueryData(['chat', sessionId], context.previousMessages);
      }
    },
    onSuccess: (data, variables, context) => {
      // Replace the optimistic message with the real one
      queryClient.setQueryData<ChatMessage[]>(['chat', sessionId], (old) => {
        if (!old) return [data.message];
        // Remove the temp message and add the real one
        // Or just map it if we can find it.
        // Simpler: Filter out the temp ID and append the real one.
        const withoutTemp = context?.tempId ? old.filter((msg) => msg.id !== context.tempId) : old;

        // Check if the real message is already there (e.g. from a poll that happened concurrently)
        if (withoutTemp.some((msg) => msg.id === data.message.id)) {
          return withoutTemp;
        }

        return [...withoutTemp, data.message];
      });
    },
    onSettled: () => {
      // Always refetch after error or success to ensure sync
      // But since we are polling, maybe we don't need to force it immediately if we trust our manual update.
      // Let's invalidate to be safe.
      queryClient.invalidateQueries({ queryKey: ['chat', sessionId] });
    },
  });
};
